import { Component, h, Fragment, ComponentInterface, State, Prop, Element } from '@stencil/core';
import { isOnScreen, _COLORS_ } from 'utils';
import { fadeUpIn } from 'animation';

declare const assetPath;

@Component({
	tag: 'kdx-card',
	styleUrl: 'kdx-card.scss',
	shadow: false
})
export class StCard implements ComponentInterface {

	@Element() root: HTMLElement;

	@State() orientation: string;

	@Prop() classes: string;
	@Prop() imgSrc: string;
	@Prop() imgAlt: string;
	@Prop() animation: string;
	@Prop() layout?: 'left' | 'right';
	@Prop() isLink = false;
	@Prop() label: string;
	@Prop() heading: string;
	@Prop() hLevel: string = 'h3';
	@Prop() articleTags: string;
	@Prop() articleDate: string;
	@Prop() articleTime: string;
	@Prop() articleAuthor: string;
	@Prop() articleHref: string;
	@Prop() listingHref: string = "/blog";


	/**
	 * Determines if the default / branded image
	 * should show if no src is explicitly defined
	 */
	@Prop() defaultImg: boolean = false;

	@Prop() ctaPrimaryText: string;
	@Prop() ctaPrimaryHref: string;
	@Prop() ctaPrimaryIcon: string;
	@Prop() ctaPrimaryAriaLabel: string;
	@Prop() ctaPrimaryStyle: string = "btn btn-primary";
	@Prop() ctaPrimaryTarget: string;
	@Prop() ctaPrimarySpeedbump: boolean;
	@Prop({
		mutable: true,
		reflect: true
	}) ctaPrimaryRel;

	@Prop() ctaSecondaryText: string;
	@Prop() ctaSecondaryHref: string;
	@Prop() ctaSecondaryIcon: string;
	@Prop() ctaSecondaryAriaLabel: string;
	@Prop() ctaSecondaryStyle: string = "btn btn-secondary";
	@Prop() ctaSecondaryTarget: string;
	@Prop() ctaSecondarySpeedbump: boolean;
	@Prop({
		mutable: true,
		reflect: true
	}) ctaSecondaryRel;

	constructor() {
		this.defaultImg = this.imgSrc ? false : true;
	}

	componentDidRender() {
		window.addEventListener('scroll', () => {
		if (this.animation) {
			if (isOnScreen(this.root)) {
			setTimeout(() => {
				fadeUpIn(this.root.querySelector(':scope > .animation'));
			}, 175);
			}
		}
		});

		globalThis.imageLoader?.initialize();		
	}

	// getSlug(str) {
	// 	return str.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
	// }

	titleHoverIn(e) {
		e.target.closest('.kdx-card').classList.add('hover');
	}

	titleHoverOut(e) {
		e.target.closest('.kdx-card').classList.remove('hover');
	}

	render() {
		return (
		<div class={`kdx-card kdx-card${this.layout ? `__img-${this.layout}` : ''} card${this.animation ? ' animation ' + this.animation : ''}${this.classes ? ` ${this.classes}` : ''}`}>
			{(this.imgSrc || this.defaultImg) && (
				<div class="card-img-top">
					{(this.imgSrc) ? (
						<div
							class="card-img lazy-load-bg"
							style={{ backgroundImage: `url(${this.imgSrc})` }}
							role="img"
							title={this.imgAlt}
							aria-describedby={this.imgAlt} 
							></div>
						) : (
							<div
							class="card-img lazy-load-bg"
							style={{ backgroundImage: `url(${assetPath}/img/logo-bg-repeater-blk@2x.png)` }}
							role="presentation"></div>
					)}
				</div>
			)}
			<div class="card-content">
				<div class="card-body">
					{this.articleTags && (
							<span class="tags">
								{JSON.parse(this.articleTags).map((tag) =>
									<a href={`${this.listingHref}${tag.FilterUrl}`} class="tag" aria-label={`click to see all articles and filter by ${tag.DisplayName}`}>{tag.DisplayName}</a>
								)}
								
							</span>
						)}

						{this.articleHref 
							? 
								<this.hLevel class="card-title">
									<a href={this.articleHref} aria-label={this.heading} onMouseEnter={this.titleHoverIn} onMouseLeave={this.titleHoverOut} onFocusin={this.titleHoverIn} onFocusout={this.titleHoverOut}>{this.heading}</a>
								</this.hLevel>
							:
								<this.hLevel class="card-title">
									{this.heading}
								</this.hLevel>
						}	
						
						{this.articleAuthor && <span class="author date-time-author">{this.articleAuthor}</span>}
						{this.articleDate && <span class="date date-time-author">{this.articleDate}</span>}
						{this.articleTime && <span class="time date-time-author">{this.articleTime}</span>}

						<slot />
				</div>

				{(this.ctaPrimaryText || this.ctaSecondaryText) && (
					<div class="card-footer ctas">
						{this.isLink
							? <>
								{
								this.ctaPrimaryText &&
									<span class={this.ctaPrimaryStyle}>
										{this.ctaPrimaryText}{(this.ctaPrimaryIcon != null) && (
											<span class="svg-icon">
												<kdx-icon icon={this.ctaPrimaryIcon} />
											</span>
										)}
									</span>
								}
								{
								this.ctaSecondaryText &&
									<span class={this.ctaSecondaryStyle}>
										{this.ctaSecondaryText}{(this.ctaSecondaryIcon != null) && (
											<span class="svg-icon">
												<kdx-icon icon={this.ctaSecondaryIcon} />
											</span>
										)}
									</span>
								}
							</>
							: <>
								{
								this.ctaPrimaryText &&
									<a
										href={this.ctaPrimaryHref}
										target={this.ctaPrimaryTarget}
										aria-label={this.ctaPrimaryAriaLabel}
										class={`${this.ctaPrimaryStyle} ${this.ctaPrimarySpeedbump ? 'speedbump-link' : ''}`}
									>
										{this.ctaPrimaryText}{(this.ctaPrimaryIcon != null) && (
											<span class="svg-icon">
												<kdx-icon icon={this.ctaPrimaryIcon} />
											</span>
										)}
									</a>
								}
								{
								this.ctaSecondaryText &&
									<a
										href={this.ctaSecondaryHref}
										target={this.ctaSecondaryTarget}
										aria-label={this.ctaSecondaryAriaLabel}
										class={`${this.ctaSecondaryStyle} ${this.ctaSecondarySpeedbump ? 'speedbump-link' : ''}`}
									>
										{this.ctaSecondaryText}{(this.ctaSecondaryIcon != null) && (
											<span class="svg-icon">
												<kdx-icon icon={this.ctaSecondaryIcon} />
											</span>
										)}
									</a>
								}
							</>
						}
					</div>
				)}
			</div>
		</div>
		);
	}
}